import React, { Component } from "react";
import PropTypes from "prop-types";
import convertToHTML from "draft-convert/lib/convertToHTML";
import selectn from "selectn";
import debounce from "lodash/debounce";
import classNames from "classnames";
import { withRouter } from "react-router-dom";

import {
  BREAKPOINT_DOWN_SMALL,
  BLOCK_TYPE,
  FACTS_MATTER_TEMPLATE,
} from "../../../config/constants";
import { styleToHTML, blockToHTML } from "./conversionHelpers";
import { entityToHTML } from "../../../utils/entityToHTML";
import { handleEmbed } from "../../../utils/embed";
import Log from "../../../utils/log";
import { MicrodataContext, HeaderContext } from "config/contexts";

import StoryNewsletterPromo from "cards/components/StoryNewsletterPromo";

import styles from "./StoryBody.css";
import keepReadingStyles from "../../../components/Library/KeepReading/KeepReading.css";

class StoryBody extends Component {
  constructor(props) {
    super(props);

    this.state = {
      isOpen: !!this.props.isOpenOnMount,
      isFixed: false,
      isClient: false,
      shouldProcess: true,
    };

    this.onKeepReadingClick = this._onKeepReadingClick.bind(this);
  }

  componentDidMount() {
    this.mediaQuery = window.matchMedia(BREAKPOINT_DOWN_SMALL);
    this.scrollListener = debounce(this.handleScroll, 50);
    this.setState({ isClient: true });
  }

  componentDidUpdate() {
    if (
      !this.state.isClient ||
      !this.processedBlocks ||
      !this.state.shouldProcess
    )
      return;

    if (this.processedBlocks.blocks) {
      this.processedBlocks.blocks
        .filter(block => block.type === BLOCK_TYPE.EMBED)
        .map(handleEmbed);
    }

    if (this.processedBlocks.hiddenBlocks) {
      this.processedBlocks.hiddenBlocks
        .filter(block => block.type === BLOCK_TYPE.EMBED)
        .map(handleEmbed);
    }

    this.setState({ shouldProcess: false });
  }

  componentWillUnmount() {
    this.removeListeners();
  }

  processBlocks = (blocks, readMoreWordCount) => {
    let newBlocks = [];
    let hiddenBlocks = [];
    let keepReading = null;
    let styledQuoteAttributions = [];
    let wordCount = 0;
    let APIWordCount = false;
    //if there is a non-null wordcount value received from the API, use it
    // and set APIWordCount flag to true
    if (readMoreWordCount !== null) {
      wordCount = readMoreWordCount;
      APIWordCount = true;
    }

    for (let i = 0; i < blocks.length; i++) {
      if (!blocks[i]) continue;
      if (
        i !== 0 &&
        blocks[i].type.toLowerCase() === BLOCK_TYPE.QUOTE_ATTRIBUTION &&
        blocks[i - 1].type.toLowerCase() === BLOCK_TYPE.STYLED_QUOTE
      ) {
        styledQuoteAttributions.push(blocks[i].key);
      }
      if (blocks[i].type.toLowerCase() === BLOCK_TYPE.KEEP_READING) {
        keepReading = blocks[i];
      } else if (keepReading) {
        hiddenBlocks.push(blocks[i]);
        if (!APIWordCount) {
          wordCount += blocks[i].text.split(" ").length;
        }
      } else {
        newBlocks.push(blocks[i]);
      }
    }

    return {
      blocks: newBlocks,
      keepReadingBlock: keepReading,
      hiddenBlocks,
      wordCount,
      styledQuoteAttributions,
    };
  };

  addListeners = () => {
    this.mediaQuery.addListener(this.handleResize);

    if (this.mediaQuery.matches) {
      window.addEventListener("scroll", this.scrollListener);
    }
  };

  removeListeners = () => {
    this.mediaQuery.removeListener(this.handleResize);
    window.removeEventListener("scroll", this.scrollListener);
  };

  handleScroll = () => {
    if (this.processedBlocks.keepReadingBlock && this.state.isOpen) {
      const hidden = document.getElementById(`hidden-${this.props.story.id}`);
      if (hidden) {
        const isFixed =
          this.mediaQuery.matches &&
          this.state.isOpen &&
          !this.isKeepReadingVisible(hidden);
        if (this.state.isFixed !== isFixed) {
          this.setState({ isFixed });
        }
      }
    }
  };

  handleResize = event => {
    if (event.matches) {
      window.addEventListener("scroll", this.scrollListener);
    } else {
      if (this.state.isFixed) {
        this.setState({ isFixed: false });
      }
      window.removeEventListener("scroll", this.scrollListener);
    }
  };

  isKeepReadingVisible = element => {
    const bounds = element.getBoundingClientRect();
    return (
      bounds.bottom <
      (window.innerHeight || document.documentElement.clientHeight)
    );
  };

  _onKeepReadingClick = () => {
    const isOpen = !this.state.isOpen;
    this.setState({ isOpen });

    if (isOpen) {
      this.addListeners();
    } else {
      this.removeListeners();

      // If next headline is not visible, scroll to just show it
      let bounds = this.keepReadingButton.getBoundingClientRect();
      let scrollValue =
        275 +
        bounds.top -
        (window.innerHeight || document.documentElement.clientHeight);

      if (scrollValue > 0) {
        window.scrollBy(0, scrollValue);
      } else if (bounds.top < 60) {
        // If keep reading button is not visible, scroll so it just shows
        window.scrollBy(0, bounds.top - 60);
      }
    }
  };

  getHTML = (blocks, entityMap, extras, headerLevels, isPrimary) => {
    let html = false;
    try {
      html = convertToHTML({
        styleToHTML,
        blockToHTML: blockToHTML(
          extras,
          this.state.isClient,
          headerLevels,
          isPrimary
        ),
        // This shouldn't normally work, but we've tricked draft-convert into requiring a version of draft-js where
        // convertToRaw is a noop - thus we can pass raw draft data right into draft-convert
        entityToHTML: entityToHTML(styles),
      })({ blocks, entityMap });
    } catch (e) {
      Log.logger(
        `draft-convert failure. story id: ${this.props.story.id}`,
        "error",
        { id: this.props.story.id, error: e }
      );
    }
    return html;
  };

  render() {
    return (
      <HeaderContext.Consumer>
        {headerLevels => {
          const currentBlocks = selectn("story.blocks.blocks", this.props);
          if (!currentBlocks) return null;
          this.processedBlocks = this.processBlocks(
            currentBlocks,
            this.props.story.read_more_wordcount
          );
          const {
            blocks,
            hiddenBlocks,
            keepReadingBlock,
            wordCount,
            styledQuoteAttributions,
          } = this.processedBlocks;
          const { entityMap } = this.props.story.blocks;
          const extras = { styledQuoteAttributions };
          const isPrimary = this.props.isPrimary;

          const html = this.getHTML(
            blocks,
            entityMap,
            extras,
            headerLevels,
            isPrimary
          );
          const keepReadingHTML =
            keepReadingBlock &&
            this.getHTML(
              [keepReadingBlock],
              entityMap,
              {
                wordCount,
                permalink: this.props.story.permalink,
              },
              headerLevels,
              isPrimary
            );
          const hiddenHTML =
            hiddenBlocks &&
            this.getHTML(
              hiddenBlocks,
              entityMap,
              extras,
              headerLevels,
              isPrimary
            );
          // don't render the story if draft-convert failed
          if (!html || (keepReadingHTML && !hiddenHTML)) return null;
          return (
            <MicrodataContext.Consumer>
              {microdata => (
                <div
                  className={classNames(styles.root, {
                    [styles.factsMatter]:
                      this.props.story.template === FACTS_MATTER_TEMPLATE,
                  })}
                  itemProp={
                    microdata && this.props.renderMicrodata
                      ? "articleBody"
                      : undefined
                  }
                >
                  <div dangerouslySetInnerHTML={{ __html: html }} />
                  {keepReadingHTML
                    ? null
                    : this.props.story.promotion &&
                      this.props.story.promotion.title && (
                        <StoryNewsletterPromo
                          promotion={this.props.story.promotion}
                        />
                      )}
                  {keepReadingHTML && (
                    <div
                      dangerouslySetInnerHTML={{ __html: keepReadingHTML }}
                      onClick={this.onKeepReadingClick}
                      ref={node => {
                        this.keepReadingButton = node;
                      }}
                      className={classNames("qa-keep-reading", {
                        [keepReadingStyles.keepReadingOpen]: this.state.isOpen,
                        [keepReadingStyles.keepReadingFixed]: this.state
                          .isFixed,
                      })}
                    />
                  )}
                  {hiddenHTML && (
                    <div
                      id={`hidden-${this.props.story.id}`}
                      className={classNames({
                        [styles.hidden]: !this.state.isOpen,
                      })}
                      style={{ paddingBottom: "65px" }}
                      aria-hidden={!this.state.isOpen}
                      aria-expanded={this.state.isOpen}
                    >
                      <div dangerouslySetInnerHTML={{ __html: hiddenHTML }} />
                      {this.props.story.promotion &&
                        this.props.story.promotion.title && (
                          <StoryNewsletterPromo
                            promotion={this.props.story.promotion}
                          />
                        )}
                    </div>
                  )}
                </div>
              )}
            </MicrodataContext.Consumer>
          );
        }}
      </HeaderContext.Consumer>
    );
  }
}

StoryBody.propTypes = {
  story: PropTypes.object.isRequired,
  storyType: PropTypes.oneOf(["newsletter"]),
  renderMicrodata: PropTypes.bool,
  isOpenOnMount: PropTypes.bool,
  isPrimary: PropTypes.bool,
};

export default withRouter(StoryBody);
